import * as React from 'react'

import { Box, Card, Hint, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { GetEmployeesRoleForReviewDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { usePerformanceModuleContext } from 'modules/performance/hooks/usePerfomanceModuleContext'
import type Review from 'modules/reviews/store/models/v2/Review'

interface Props {
  review?: Review | null
  revieweesIds: Set<string>
}

const CompetencyAssessmentToggle = ({ review, revieweesIds }: Props) => {
  const { performanceFeatureAvailability } = usePerformanceModuleContext()
  const disabled = !review?.isDraft
  const showToggle = performanceFeatureAvailability.canSeeCompetencies && review
  const [seeCompetencies, setSeeCompetencies] = React.useState(false)
  const { data } = useQuery(GetEmployeesRoleForReviewDocument, {
    variables: { accessIds: [...revieweesIds].map((x) => Number(x)) },
    skip: !showToggle || disabled,
  })
  const employees = data?.employees.employees
  const onSubmit = async () => {
    if (!review) {
      return
    }

    const { success } = await asyncAction(() =>
      review.update({
        setting: { include_competencies_assessments: !review?.assessCompetencies },
      })
    )

    if (success) {
      setSeeCompetencies(review?.assessCompetencies)
    } else {
      setSeeCompetencies(!review?.assessCompetencies)
    }
    track(trackingEvents.reviews.enableCompetencyAssessmentInReview)
  }

  if (!showToggle) {
    return null
  }

  const employeesWithoutRoleCompetencies = employees?.filter(
    (e) => !e.referenceContract?.jobCatalogLevel?.role?.competencies?.length
  )

  const showHint = seeCompetencies && !!employees && employeesWithoutRoleCompetencies?.length

  const handleHintClose = () => {
    track(trackingEvents.reviews.closeCompetenciesToRolesHint)
  }
  return (
    <Box>
      <Hint.Popover.Provider onClose={handleHintClose} steps={1}>
        <Hint.Popover
          step={1}
          title={i18n.t('surveys.review.competencies.hint_title')}
          description={i18n.t('surveys.review.competencies.hint_description', {
            with_competency: employeesWithoutRoleCompetencies?.length ?? 0,
            total: employees?.length ?? 0,
          })}
          position="left top"
          actionLabel={i18n.t('common.close')}
          isHidden={!showHint}
        >
          <Card>
            <Card.Title
              action={
                <TogglerCheckbox
                  value={review?.assessCompetencies || false}
                  onChange={onSubmit}
                  disabled={disabled}
                />
              }
            >
              {i18n.t('surveys.review.competencies.title')}
            </Card.Title>
            <Card.Description>{i18n.t('surveys.review.competencies.description')}</Card.Description>
          </Card>
        </Hint.Popover>
      </Hint.Popover.Provider>
    </Box>
  )
}

export default observer(CompetencyAssessmentToggle)
